import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Activity, CalendarDays, Check, ChevronRight, Clock, Plus, Sparkles, Star, Swords, Trophy, Users, X } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { supabaseV2 } from "../../lib/supabase";
import { currentIntlLocale } from "../../lib/i18n";
import { calculatePeriodSquadScore } from "../../lib/squad-score";
import { usePermissions } from "../../lib/permissions";
import { isSquadPlayer } from "../../lib/roster";
import { useWorkspaceData } from "../../lib/workspace-data";
import { seasonReferenceNow, useSeason } from "../../lib/seasons";
import { SectionLoading } from "../../components/state";
import { TeamInviteLinkCard } from "../admin/TeamInviteLinkCard";
import type { View } from "../../components/shell/nav-config";
import type { Player, Session, SessionResponse, SquadScoreSetting, Team } from "../../types";

export function HomeView({
  team,
  onOpenPlayer,
  onChangeView,
}: {
  team: Team;
  onOpenPlayer: (playerId: string) => void;
  onChangeView: (view: View) => void;
}) {
  const { t } = useTranslation("home");
  const { canManage, canManageMembers } = usePermissions();
  const { players, events, attendance: responses, matches, matchStats, isLoading } = useWorkspaceData(team.id);
  const { selectedSeason } = useSeason();
  const lifetimeQuery = useTeamLifetimeCounts(team.id);
  const activeSettingQuery = useQuery({
    queryKey: ["squad-score-settings", team.id, "active"],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("squad_score_settings")
        .select("*")
        .eq("team_id", team.id)
        .eq("is_active", true)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data || null) as SquadScoreSetting | null;
    },
    staleTime: 60_000,
  });
  const activeSetting = activeSettingQuery.data;

  const eaClubQuery = useQuery({
    queryKey: ["ea-club", team.id],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("latest_ea_club", { _team_id: team.id });
      if (error) throw error;
      return (data as any) || null;
    },
    enabled: !!team.ea_club_id,
    staleTime: 60_000,
  });
  const eaOverall = (eaClubQuery.data?.overallStats as any) || null;

  if (isLoading) return <SectionLoading />;

  const now = Date.now();
  const upcoming = events
    .filter((event) => new Date(event.starts_at).getTime() >= now && event.status !== "cancelled" && event.status !== "draft");
  const nextEvent = upcoming[0];
  const roster = players.filter(isSquadPlayer);
  const responsesOf = (sessionId: string) => responses.filter((r) => r.session_id === sessionId && r.status !== "no_response");
  const answered = (sessionId: string) => responsesOf(sessionId).length;

  const present = responses.filter((r) => r.status === "present").length;
  const late = responses.filter((r) => r.status === "late").length;
  const absent = responses.filter((r) => r.status === "absent").length;
  const totalResponses = present + late + absent;
  const presencePct = totalResponses > 0 ? Math.round(((present + late) / totalResponses) * 100) : null;
  const teamGoals = matches.reduce((acc, m) => acc + (m.our_score || 0), 0);
  const teamGoalsAgainst = matches.reduce((acc, m) => acc + (m.opponent_score || 0), 0);
  const completed = matches.filter((m) => m.our_score !== null && m.opponent_score !== null);
  const wins = completed.filter((m) => Number(m.our_score) > Number(m.opponent_score)).length;
  const draws = completed.filter((m) => Number(m.our_score) === Number(m.opponent_score)).length;
  const losses = completed.filter((m) => Number(m.our_score) < Number(m.opponent_score)).length;
  const missingResult = matches.filter((m) => m.starts_at && new Date(m.starts_at).getTime() < now && (m.our_score === null || m.opponent_score === null)).length;

  // I dati sono gia' filtrati sulla stagione selezionata: lo Squad Score copre tutta la stagione.
  const scoreReferenceNow = selectedSeason?.status === "closed" ? seasonReferenceNow(selectedSeason) : undefined;
  const topScores = activeSetting
    ? roster
        .map((player) => {
          const { score, eventsCount } = calculatePeriodSquadScore(
            responses.filter((r) => r.player_id === player.id),
            matchStats.filter((s) => s.player_id === player.id),
            activeSetting,
            { fromMs: null, toMs: null },
            events.length,
            scoreReferenceNow,
          );
          return { player, score, events: eventsCount };
        })
        .filter((row) => row.score > 0 || row.events > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 5)
    : [];

  // Card invito in home: ha senso solo se la rosa è ancora piccola. Con 5+
  // giocatori il link generico non aiuta e resta in Impostazioni.
  const showInviteCard = canManageMembers && roster.length < 5;
  const showFirstSessionCard = canManage && lifetimeQuery.data?.sessions === 0 && events.length === 0 && roster.length >= 1;
  const intlLocale = currentIntlLocale();

  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start animate-fade-in">
      <div className="space-y-4 min-w-0">
        {nextEvent ? (
          <NextSessionCard
            team={team}
            event={nextEvent}
            roster={roster}
            responses={responsesOf(nextEvent.id)}
            opponents={matches.filter((m) => m.session_id === nextEvent.id).map((m) => m.opponent_name).filter(Boolean) as string[]}
            onOpen={() => onChangeView("sessions")}
          />
        ) : (
          <section className="rounded-[20px] bg-card p-5">
            <h2 className="text-[17px] font-semibold">{t("next.emptyTitle")}</h2>
            <p className="mt-1 text-muted-foreground">{t("next.emptyDesc")}</p>
            {canManage && (
              <Button className="mt-4" onClick={() => onChangeView("sessions")}>
                <Plus className="h-4 w-4" /> {t("newSession")}
              </Button>
            )}
          </section>
        )}
        <PreSeasonCard
          team={team}
          players={players}
          events={events}
          matches={matches}
          activeSetting={activeSetting}
          onChangeView={onChangeView}
        />
        {showInviteCard && <TeamInviteLinkCard teamId={team.id} teamName={team.name} />}
        {showFirstSessionCard && <FirstSessionCard onChangeView={onChangeView} />}
        {upcoming.length > 1 && (
          <section className="rounded-[20px] bg-card p-5 pb-2">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-[17px] font-semibold">{t("calendar.title")}</h2>
              <button type="button" onClick={() => onChangeView("sessions")} className="text-sm text-muted-foreground hover:text-foreground">{t("calendar.all")}</button>
            </div>
            <ul className="mt-2">
              {upcoming.slice(1, 5).map((event) => {
                const date = new Date(event.starts_at);
                return (
                  <li key={event.id} className="border-t first:border-t-0">
                    <button type="button" onClick={() => onChangeView("sessions")} className="w-full py-3 flex items-center gap-4 text-left">
                      <span className="w-11 shrink-0 text-center">
                        <span className="eb-data block text-[13px] text-[hsl(var(--faint))]">{date.toLocaleDateString(intlLocale, { weekday: "short" }).replace(".", "")}</span>
                        <span className="eb-num block text-[30px]">{date.getDate()}</span>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium truncate">{event.title}</span>
                        <span className="block text-[13px] text-[hsl(var(--faint))]">
                          {date.toLocaleTimeString(intlLocale, { hour: "2-digit", minute: "2-digit" })} · {event.has_matches ? t("events.matchDay") : t("events.training")}
                        </span>
                      </span>
                      <span className="shrink-0 text-right">
                        <span className="block font-medium tabular-nums">{answered(event.id)}/{roster.length}</span>
                        <span className="block text-[13px] text-[hsl(var(--faint))]">{t("calendar.responses")}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>

      <aside className="space-y-4 min-w-0">
        {canManage && missingResult > 0 && (
          <section className="rounded-[20px] bg-card p-5">
            <h2 className="text-[17px] font-semibold">{t("todo.title")}</h2>
            <div className="mt-3 flex items-start gap-3">
              <span aria-hidden className="mt-1 h-3.5 w-2.5 rounded-[3px] bg-[hsl(var(--warn))] shrink-0" />
              <p className="flex-1 font-medium">{t("todo.missingResult", { count: missingResult })}</p>
              <button type="button" onClick={() => onChangeView("sessions")} className="text-sm font-semibold shrink-0">{t("todo.fix")}</button>
            </div>
          </section>
        )}
        <section className="rounded-[20px] bg-card p-5">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-[17px] font-semibold">{selectedSeason ? t("season.title", { name: selectedSeason.name }) : t("eyebrow")}</h2>
            <span className="text-[13px] text-[hsl(var(--faint))]">{t("season.matches", { count: completed.length })}</span>
          </div>
          <dl className="mt-4 grid grid-cols-4 gap-2">
            {([[t("season.wins"), wins, "text-[hsl(var(--ok))]"], [t("season.draws"), draws, ""], [t("season.losses"), losses, ""], [t("season.goals"), `${teamGoals}-${teamGoalsAgainst}`, ""]] as const).map(([label, value, tone]) => (
              <div key={label} className="min-w-0">
                <dd className={`eb-num text-[34px] pr-1 ${tone}`}>{value}</dd>
                <dt className="text-[13px] text-[hsl(var(--faint))]">{label}</dt>
              </div>
            ))}
          </dl>
          <div className="mt-4 pt-3 border-t flex items-baseline justify-between gap-3 text-sm">
            <span className="text-muted-foreground">{t("season.presence")}</span>
            <span className="font-medium tabular-nums">{presencePct === null ? "-" : `${presencePct}%`}</span>
          </div>
        </section>
        {topScores.length > 0 && (
          <section className="rounded-[20px] bg-card p-5">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-[17px] font-semibold">{t("top.title")}</h2>
              <button type="button" onClick={() => onChangeView("stats")} className="text-sm text-muted-foreground hover:text-foreground">{t("top.ranking")}</button>
            </div>
            <ol className="mt-2">
              {topScores.map((row, index) => (
                <li key={row.player.id}>
                  <button type="button" onClick={() => onOpenPlayer(row.player.id)} className="w-full py-2 flex items-center gap-3 text-left">
                    <span className="eb-num text-[22px] w-8 shrink-0 text-[hsl(var(--faint))]">{row.player.shirt_number ?? "-"}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium truncate">{row.player.display_name}</span>
                      <span className="block text-[13px] text-[hsl(var(--faint))]">{t("top.events", { count: row.events })}</span>
                    </span>
                    <span className={`eb-num text-[26px] pr-1 ${index === 0 ? "text-[hsl(var(--ok))]" : ""}`}>{row.score.toFixed(0)}</span>
                  </button>
                </li>
              ))}
            </ol>
          </section>
        )}
        {team.ea_club_id && (
          <button type="button" onClick={() => onChangeView("ea")} className="w-full rounded-[20px] bg-card p-5 text-left">
            <span className="flex items-center justify-between gap-3">
              <span className="text-[17px] font-semibold">{t("mondofc.short")}</span>
              <ChevronRight className="h-4 w-4 text-[hsl(var(--faint))]" />
            </span>
            {eaOverall && (
              <span className="mt-1 block text-[13px] text-muted-foreground tabular-nums">
                {t("mondofc.summary", { rating: eaOverall.skillRating || "-", games: Number(eaOverall.gamesPlayed || 0), wins: Number(eaOverall.wins || 0), draws: Number(eaOverall.ties || 0), losses: Number(eaOverall.losses || 0) })}
              </span>
            )}
          </button>
        )}
      </aside>
    </div>
  );
}

function NextSessionCard({
  team, event, roster, responses, opponents, onOpen,
}: {
  team: Team;
  event: Session;
  roster: Player[];
  responses: SessionResponse[];
  opponents: string[];
  onOpen: () => void;
}) {
  const { t } = useTranslation("home");
  const intlLocale = currentIntlLocale();
  const date = new Date(event.starts_at);
  const byPlayer = new Map(responses.map((r) => [r.player_id, r.status]));
  const count = (status: string) => responses.filter((r) => r.status === status).length;
  const pending = roster.filter((p) => !byPlayer.has(p.id)).length;
  const dayDiff = Math.round((new Date(date).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86_400_000);
  const when = dayDiff === 0 ? t("next.today") : dayDiff === 1 ? t("next.tomorrow") : null;
  const deadline = event.response_deadline_at ? new Date(event.response_deadline_at) : null;
  const dots = [...roster].sort((a, b) => order(byPlayer.get(a.id)) - order(byPlayer.get(b.id)));
  return (
    <section className="relative rounded-[20px] bg-card p-5 lg:p-6 overflow-hidden">
      <svg aria-hidden viewBox="0 0 160 110" className="absolute right-0 top-0 w-[150px] lg:w-[190px]">
        <path d="M18 0h62l80 84v26z" fill={team.primary_color || "#005F7E"} />
        <path d="M94 0h24l42 44v26z" fill={team.secondary_color || "#004272"} />
      </svg>
      <div className="relative lg:flex lg:gap-10">
        <div className="min-w-0">
          <p className="flex items-baseline gap-2">
            {when && <span className="font-medium">{when}</span>}
            <span className="eb-data text-[15px] text-muted-foreground">{date.toLocaleDateString(intlLocale, { weekday: "short", day: "numeric", month: "short" }).replace(",", "")}</span>
          </p>
          <p className="eb-num text-[72px] lg:text-[92px] pr-2 mt-1">{date.toLocaleTimeString(intlLocale, { hour: "2-digit", minute: "2-digit" })}</p>
          <p className="mt-1 text-muted-foreground">{opponents.length > 0 ? t("next.matches", { count: opponents.length, names: opponents.join(", ") }) : event.title}</p>
        </div>
        <div className="mt-5 lg:mt-0 lg:flex-1 min-w-0">
          <ul aria-hidden className="flex flex-wrap gap-1.5">
            {dots.map((p) => <li key={p.id} className={`h-[18px] w-[18px] rounded-full ${dotClass(byPlayer.get(p.id))}`} title={p.display_name} />)}
          </ul>
          <p className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><span className="eb-dot dot-ok" />{t("next.present", { count: count("present") })}</span>
            <span className="inline-flex items-center gap-1.5"><span className="eb-dot dot-warn" />{t("next.late", { count: count("late") })}</span>
            <span className="inline-flex items-center gap-1.5"><span className="eb-dot dot-danger" />{t("next.absent", { count: count("absent") })}</span>
          </p>
          <p className="mt-2 flex items-center gap-1.5 font-medium"><span className="eb-dot dot-muted" />{t("next.pending", { count: pending })}</p>
          {deadline && (
            <p className="mt-3 flex items-center gap-1.5 text-[13px] text-muted-foreground">
              <Clock className="h-4 w-4 text-[hsl(var(--warn))]" />
              {t("next.deadline", { date: deadline.toLocaleString(intlLocale, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) })}
            </p>
          )}
          <Button className="mt-5 flex w-full lg:w-auto" onClick={onOpen}>{t("next.open")}<ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
    </section>
  );
}

const order = (status?: string) => (status === "present" ? 0 : status === "late" ? 1 : status === "absent" ? 2 : 3);
const dotClass = (status?: string) =>
  status === "present" ? "bg-[hsl(var(--ok))]"
    : status === "late" ? "bg-[hsl(var(--warn))] rounded-[5px]"
      : status === "absent" ? "bg-[hsl(var(--danger))] rounded-[5px]"
        : "shadow-[inset_0_0_0_1.5px_hsl(var(--input))]";

function PreSeasonCard({
  team,
  players,
  events,
  matches,
  activeSetting,
  onChangeView,
}: {
  team: Team;
  players: Array<any>;
  events: Array<any>;
  matches: Array<any>;
  activeSetting: SquadScoreSetting | null;
  onChangeView: (view: View) => void;
}) {
  const { t } = useTranslation("home");
  const dismissKey = `eb-preseason-dismissed:${team.id}`;
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(window.localStorage.getItem(dismissKey) === "1");
    } catch {
      setDismissed(false);
    }
  }, [dismissKey]);

  // Esiste almeno una sessione con convocazioni gia' notificate (notified_at IS NOT NULL)
  const callupsNotifiedQuery = useQuery({
    queryKey: ["preseason-callups-notified", team.id],
    queryFn: async () => {
      const { count, error } = await supabaseV2
        .from("session_callups")
        .select("id, sessions!inner(team_id)", { count: "exact", head: true })
        .eq("sessions.team_id", team.id)
        .not("notified_at", "is", null);
      if (error) throw error;
      return (count || 0) > 0;
    },
    staleTime: 30_000,
  });

  // Missioni di onboarding: contano su tutta la storia della squadra, non
  // sulla stagione selezionata (dopo una chiusura non devono ripartire).
  const lifetimeQuery = useTeamLifetimeCounts(team.id);

  const rosterNonGuest = players.filter(isSquadPlayer);
  const rosterLinked = rosterNonGuest.filter((p: any) => !!p.user_id).length;
  const rosterDone = rosterLinked >= Math.max(5, Math.ceil(rosterNonGuest.length * 0.5));

  const firstSessionDone = events.length > 0 || (lifetimeQuery.data?.sessions || 0) > 0;
  const firstMatchDayDone =
    matches.length > 0 || events.some((e: any) => e.has_matches) || (lifetimeQuery.data?.matchDays || 0) > 0;
  const eaDone = !!team.ea_club_id;
  const firstCallupDone = !!callupsNotifiedQuery.data;
  const squadScoreDone = !!activeSetting;

  const missions = [
    {
      key: "roster",
      done: rosterDone,
      view: "players" as View,
      icon: Users,
    },
    {
      key: "firstSession",
      done: firstSessionDone,
      view: "sessions" as View,
      icon: CalendarDays,
    },
    {
      key: "firstCallup",
      done: firstCallupDone,
      view: "sessions" as View,
      icon: Activity,
    },
    {
      key: "firstMatchDay",
      done: firstMatchDayDone,
      view: "sessions" as View,
      icon: Swords,
    },
    {
      key: "ea",
      done: eaDone,
      view: "settings" as View,
      icon: Trophy,
    },
    {
      key: "squadScore",
      done: squadScoreDone,
      view: "settings" as View,
      icon: Star,
    },
  ];
  const doneCount = missions.filter((m) => m.done).length;
  const total = missions.length;
  const allDone = doneCount === total;
  const moodKey = `preseason.mood${Math.min(doneCount, 6)}` as const;

  if (dismissed || allDone) return null;

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-start gap-2">
        <div className="flex-1">
          <CardDescription className="uppercase tracking-wider text-xs font-semibold">
            {t("preseason.title")}
          </CardDescription>
          <div className="flex items-baseline gap-2 mt-1">
            <CardTitle className="text-2xl tabular-nums">
              {doneCount}/{total}
            </CardTitle>
            <p className="text-xs text-muted-foreground">{t("preseason.progress", { done: doneCount, total })}</p>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{t(moodKey)}</p>
          <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-gradient-primary transition-all"
              style={{ width: `${(doneCount / total) * 100}%` }}
            />
          </div>
        </div>
        <button
          type="button"
          aria-label={t("preseason.dismiss")}
          onClick={() => {
            try {
              window.localStorage.setItem(dismissKey, "1");
            } catch {
              /* ignore */
            }
            setDismissed(true);
          }}
          className="h-7 w-7 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {missions.map((m) => {
          const Icon = m.icon;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => onChangeView(m.view)}
              className={
                "text-left rounded-xl border bg-card hover:border-primary hover:bg-muted/40 transition-colors px-3 py-2.5 flex items-start gap-2.5 " +
                (m.done ? "opacity-60" : "")
              }
            >
              <div
                className={
                  "h-7 w-7 shrink-0 rounded-full inline-flex items-center justify-center " +
                  (m.done ? "bg-emerald-500/15 text-emerald-600" : "bg-primary/10 text-primary")
                }
              >
                {m.done ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-tight">
                  {t(`preseason.missions.${m.key}.title`)}
                </p>
                <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                  {t(`preseason.missions.${m.key}.body`)}
                </p>
                <div className="mt-1 inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider text-primary font-semibold">
                  {m.done ? t("preseason.done") : t("preseason.go")}
                  {!m.done && <ChevronRight className="h-3 w-3" />}
                </div>
              </div>
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
}

function FirstSessionCard({ onChangeView }: { onChangeView: (view: View) => void }) {
  const { t } = useTranslation("home");
  return (
    <Card>
      <CardContent className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 py-4">
        <div className="h-10 w-10 shrink-0 rounded-full bg-accent/15 text-accent grid place-items-center">
          <CalendarDays className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-base">{t("firstSessionCard.title")}</p>
          <p className="text-sm text-muted-foreground mt-0.5">{t("firstSessionCard.desc")}</p>
        </div>
        <Button
          size="sm"
          className="btn-lift shrink-0 self-stretch sm:self-auto"
          onClick={() => onChangeView("sessions")}
        >
          <Plus className="h-3.5 w-3.5" />
          {t("firstSessionCard.cta")}
        </Button>
      </CardContent>
    </Card>
  );
}

/** Conteggi su tutta la storia della squadra (indipendenti dalla stagione). */
function useTeamLifetimeCounts(teamId: string) {
  return useQuery({
    queryKey: ["team-lifetime-counts", teamId],
    queryFn: async () => {
      const [all, withMatches] = await Promise.all([
        supabaseV2.from("sessions").select("id", { count: "exact", head: true }).eq("team_id", teamId),
        supabaseV2.from("sessions").select("id", { count: "exact", head: true }).eq("team_id", teamId).eq("has_matches", true),
      ]);
      if (all.error) throw all.error;
      if (withMatches.error) throw withMatches.error;
      return { sessions: all.count || 0, matchDays: withMatches.count || 0 };
    },
    staleTime: 30_000,
  });
}
